import { Container } from "@pixi/display";
import { IDestroyOptions, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { nameof } from "bindable-data";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { MarketplaceBuildingItem } from "../../main/models/building/MarketplaceBuildingItem";
import { DiscountResourcePackage } from "../../mydiscount/resources/DiscountResourcePackage";
import { IUseDiscountViewModel } from "../model/IUseDiscountViewModel";
import { MonField } from "../../../../common/components/MonField";

export class BuildingPriceInfoContainter extends Container {
    private readonly discountResources: DiscountResourcePackage;
    private readonly useDiscountVIewModel: IUseDiscountViewModel;
    private readonly currentMpBuildingItem: MarketplaceBuildingItem;

    private buildingPriceText: Text;
    private initialDollarText: Text;
    private priceWithDiscountText: Text;
    private discountIcon: Sprite;
    private appliedText: Text;
    private appliedDollarText: Text;
    private initialMonPrice: MonField;
    private appliedMonField: MonField;

    public constructor(
        discountResources: DiscountResourcePackage,
        useDiscountVIewModel: IUseDiscountViewModel,
        currentMpBuildingItem: MarketplaceBuildingItem
    ) {
        super();
        this.discountResources = discountResources;
        this.useDiscountVIewModel = useDiscountVIewModel;
        this.currentMpBuildingItem = currentMpBuildingItem;
        this.build();
        this.useDiscountVIewModel.propertyChanged.add(this.onUseDiscountVIewModelPropertyChanged, this);
    }

    private async onUseDiscountVIewModelPropertyChanged(model: IUseDiscountViewModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "summaryDiscountPercentage"):
                const summaryDiscountPercentage = this.useDiscountVIewModel.summaryDiscountPercentage.value;
                if (this.currentMpBuildingItem.price.mon != null) {
                    const mon = this.currentMpBuildingItem.price.mon;
                    let appliedPrice = Math.max(Number(mon * summaryDiscountPercentage), 0);
                    this.appliedMonField.updatePrice(appliedPrice);
                    this.appliedText.position.set(this.width * 0.5 - (this.appliedText.width + this.appliedMonField.width) * 0.5 + 2, 123);
                    this.appliedMonField.position.set(this.appliedText.x + this.appliedText.width + 4, 123);
                } else {
                    const usd = this.currentMpBuildingItem.price.usd;
                    let appliedPrice = Math.max(Number(usd * summaryDiscountPercentage), 0);
                    this.appliedDollarText.text = "$" + appliedPrice.toFixed(2).replace(/(\.0+|0+)$/, "");
                    this.appliedText.position.set(this.width * 0.5 - (this.appliedText.width + this.appliedDollarText.width) * 0.5 + 2, 126);
                    this.appliedDollarText.position.set(this.appliedText.x + this.appliedText.width + 4, 123);
                }
                break;
        }
    }

    private build(): void {
        this.buildingPriceText = new Text(`INITIAL BUILDING PRICE:`, new DefaultDiscountTextStyle());
        this.addChild(this.buildingPriceText);

        if (this.currentMpBuildingItem.price.mon != null) {
            this.initialMonPrice = new MonField(this.currentMpBuildingItem.price.mon, this.discountResources.assets_views_discount_mon_icon_png, new MonTextStyle(), 10);
            this.initialMonPrice.position.x = 349;
            this.addChild(this.initialMonPrice);
        } else {
            this.initialDollarText = new Text(`$${this.currentMpBuildingItem.price.usd}`, new DefaultDiscountTextStyle());
            this.initialDollarText.style.fill = ["#a9fd0b", "#87f30c"];
            this.initialDollarText.position.x = 349.5;
            this.addChild(this.initialDollarText);
        }

        this.priceWithDiscountText = new Text(`PRICE WITH DISCOUNT`, new DefaultDiscountTextStyle());
        this.addChild(this.priceWithDiscountText);
        this.discountIcon = new Sprite(this.discountResources.assets_views_discount_discount_coupon_png);
        this.discountIcon.width = this.discountIcon.height = 60;
        this.addChild(this.discountIcon);
        this.priceWithDiscountText.position.set(this.width * 0.5 - (this.priceWithDiscountText.width + this.discountIcon.width) * 0.5, 61);
        this.discountIcon.position.set(this.priceWithDiscountText.x + this.priceWithDiscountText.width + 2, 48.5);
        this.appliedText = new Text(`APPLIED:`, new DefaultDiscountTextStyle());
        this.addChild(this.appliedText);
        const summaryDiscountPercentage = this.useDiscountVIewModel.summaryDiscountPercentage.value;

        if (this.currentMpBuildingItem.price.mon != null) {
            const mon = this.currentMpBuildingItem.price.mon;
            let appliedPrice = Math.max(Number(mon * summaryDiscountPercentage), 0);
            this.appliedMonField = new MonField(appliedPrice, this.discountResources.assets_views_discount_mon_icon_png, new MonTextStyle(), 10);
            this.appliedText.position.set(this.width * 0.5 - (this.appliedText.width + this.appliedMonField.width) * 0.5 + 2, 123);
            this.appliedMonField.position.set(this.appliedText.x + this.appliedText.width + 4, 123);
            this.addChild(this.appliedMonField);
        } else {
            const usd = this.currentMpBuildingItem.price.usd;
            let appliedPrice = Math.max(Number(usd * summaryDiscountPercentage), 0);
            this.appliedDollarText = new Text("$" + appliedPrice.toFixed(2).replace(/(\.0+|0+)$/, ""), new DefaultDiscountTextStyle());
            this.appliedDollarText.style.fill = ["#a9fd0b", "#87f30c"];
            this.appliedText.position.set(this.width * 0.5 - (this.appliedText.width + this.appliedDollarText.width) * 0.5 + 2, 126);
            this.appliedDollarText.position.set(this.appliedText.x + this.appliedText.width + 4, 123);
            this.addChild(this.appliedDollarText);
        }
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.useDiscountVIewModel.propertyChanged.remove(this.onUseDiscountVIewModelPropertyChanged, this);
        super.destroy(options);
    }
}

class DefaultDiscountTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48
        }));
    }
}

class MonTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fill: [
                "#FFE302",
                "#FF9024"
            ],
            fillGradientStops: [0.2],
            fontSize: 48,
            align: "center"
        });
    }
}
